// An exercise's working record, shown in the exercise panel: who took part (members or anyone
// else by name, with attendance), the findings each raised as a corrective action, and the
// scenario library "New from template" starts from. The API is routes/resilienceRecords.routes.ts;
// the library is frontend/src/lib/exerciseScenarios.ts (mirror of the backend's).

import { useCallback, useEffect, useState } from "react";
import { theme, card, btnPrimary, type as text } from "../../theme";
import { api, apiErrorMessage } from "../../api/client";
import { PortalLink } from "../../components/PortalLink";
import { KIND_HREFS } from "../../components/EntityTags";
import { EXERCISE_SCENARIOS, type ExerciseScenario } from "../../lib/exerciseScenarios";
import { EditButton, RecordActions } from "../../components/RecordView";
import { EXERCISE_KINDS, labelOf, SeverityPill, StatusPill, ModalOverlay, ModalHeader, Field, input, textarea, secondaryBtn, dangerBtn, type Option } from "./shared";

// ── Insights (GET /resilience/exercise-insights) ─────────────────────────────

export interface ExerciseInsights {
  perExercise: Record<string, { findings: number; critical: number; withoutAction: number; participants: number }>;
  findingsWithoutAction: number;
  criticalAssetsNeverExercised: Array<{ id: string; name: string; criticality: string; isEssentialFunction: boolean }>;
}

export function useExerciseInsights(refreshKey: unknown): ExerciseInsights | null {
  const [data, setData] = useState<ExerciseInsights | null>(null);
  useEffect(() => {
    let live = true;
    api.get<ExerciseInsights>("/resilience/exercise-insights").then((d) => { if (live) setData(d); }).catch(() => { if (live) setData(null); });
    return () => { live = false; };
  }, [refreshKey]);
  return data;
}

const h3: React.CSSProperties = { ...text.sectionTitle, margin: "0 0 8px" };

// ── Scenario library ─────────────────────────────────────────────────────────

/** Pick a template; the caller pre-fills the exercise form from it. */
export function ExerciseScenarioPicker({ onPick, onClose }: { onPick: (s: ExerciseScenario) => void; onClose: () => void }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader title="New exercise from a template" onClose={onClose} />
      <div style={{ padding: 20, display: "grid", gap: 10 }}>
        <p style={{ ...text.body, margin: 0, color: theme.color.muted }}>Each template carries the scenario, objectives, who should take part, the timed prompts a facilitator reads out and how to judge the result. Everything can be edited before saving.</p>
        {EXERCISE_SCENARIOS.map((s) => (
          <div key={s.id} style={{ ...card, padding: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={text.itemTitle}>{s.title}</div>
                <div style={text.hint}>{labelOf(EXERCISE_KINDS, s.kind)} · about {s.durationMinutes >= 120 ? `${Math.round(s.durationMinutes / 60)} hours` : `${s.durationMinutes} minutes`} · {s.injects.length} injects</div>
              </div>
              <button type="button" style={secondaryBtn} onClick={() => setOpen(open === s.id ? null : s.id)}>{open === s.id ? "Hide detail" : "Preview"}</button>
              <button type="button" style={btnPrimary} onClick={() => onPick(s)}>Use this template</button>
            </div>
            <div style={{ ...text.body, color: theme.color.muted, marginTop: 8, lineHeight: 1.5 }}>{s.scenario}</div>
            {open === s.id && (
              <div style={{ ...text.hint, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, marginTop: 10, lineHeight: 1.5 }}>
                <div><strong>Objectives</strong><ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{s.objectives.map((o) => <li key={o}>{o}</li>)}</ul></div>
                <div><strong>Success criteria</strong><ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{s.successCriteria.map((o) => <li key={o}>{o}</li>)}</ul></div>
                <div><strong>Who takes part</strong><div style={{ marginTop: 4 }}>{s.participantRoles.join(", ")}</div></div>
                <div><strong>Injects</strong><ol style={{ margin: "4px 0 0", paddingLeft: 18 }}>{s.injects.map((i) => <li key={i.atMinutes}>+{i.atMinutes} min: {i.prompt}</li>)}</ol></div>
              </div>
            )}
          </div>
        ))}
      </div>
    </ModalOverlay>
  );
}

// ── Participants ─────────────────────────────────────────────────────────────

interface Participant { id?: string; userId: string | null; name: string; role: string | null; attended: boolean | null }

export function ExerciseParticipants({ exerciseId, canEdit, members, suggestedRoles, onChanged }: {
  exerciseId: string; canEdit: boolean; members: Option[]; suggestedRoles: string[]; onChanged: () => void;
}) {
  const [rows, setRows] = useState<Participant[] | null>(null);
  const [draft, setDraft] = useState<Participant[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try { setRows((await api.get<{ participants: Participant[] }>(`/resilience/exercises/${exerciseId}/participants`)).participants); }
    catch (e) { setError(apiErrorMessage(e, "Could not load participants")); setRows([]); }
  }, [exerciseId]);
  useEffect(() => { void load(); }, [load]);

  async function save() {
    if (!draft) return;
    setSaving(true); setError(null);
    try {
      const res = await api.put<{ participants: Participant[] }>(`/resilience/exercises/${exerciseId}/participants`, {
        participants: draft.filter((p) => p.userId || p.name.trim()).map((p) => ({ userId: p.userId || null, name: p.name.trim() || null, role: p.role?.trim() || null, attended: p.attended })),
      });
      setRows(res.participants); setDraft(null); onChanged();
    } catch (e) { setError(apiErrorMessage(e, "Could not save participants")); }
    finally { setSaving(false); }
  }

  const set = (i: number, patch: Partial<Participant>) => setDraft((d) => d!.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const attended = (rows ?? []).filter((p) => p.attended === true).length;
  return (
    <section style={{ ...card, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <h3 style={{ ...h3, margin: 0 }}>Participants</h3>
        {rows && rows.length > 0 && <span style={text.hint}>{rows.length} named, {attended} attended</span>}
        <span style={{ flex: 1 }} />
        {canEdit && <EditButton label={rows?.length ? "Edit participants" : "Add participants"} onClick={() => setDraft(rows?.length ? rows.map((r) => ({ ...r })) : [{ userId: null, name: "", role: suggestedRoles[0] ?? null, attended: null }])} />}
      </div>
      {error && !draft && <div style={{ ...text.hint, color: theme.color.dangerText, marginBottom: 6 }}>{error}</div>}
      {draft && (
        <ModalOverlay onClose={() => setDraft(null)} wide>
          <ModalHeader title="Participants" onClose={() => setDraft(null)} />
          <div style={{ padding: 20, display: "grid", gap: 8 }}>
            <datalist id={`roles-${exerciseId}`}>{suggestedRoles.map((r) => <option key={r} value={r} />)}</datalist>
            {draft.map((p, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 6, alignItems: "center" }}>
                <select value={p.userId ?? ""} onChange={(e) => set(i, { userId: e.target.value || null, name: e.target.value ? (members.find((m) => m.id === e.target.value)?.name ?? "") : p.name })} style={input} aria-label="Member">
                  <option value="">Someone without a login</option>
                  {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <input value={p.name} onChange={(e) => set(i, { name: e.target.value })} style={input} placeholder="Name" disabled={!!p.userId} aria-label="Name" />
                <input value={p.role ?? ""} onChange={(e) => set(i, { role: e.target.value })} style={input} placeholder="Role" list={`roles-${exerciseId}`} aria-label="Role" />
                <select value={p.attended == null ? "" : p.attended ? "yes" : "no"} onChange={(e) => set(i, { attended: e.target.value === "" ? null : e.target.value === "yes" })} style={input} aria-label="Attended">
                  <option value="">Attendance not recorded</option><option value="yes">Attended</option><option value="no">Did not attend</option>
                </select>
                <button type="button" style={dangerBtn} onClick={() => setDraft((d) => d!.filter((_, j) => j !== i))}>Remove</button>
              </div>
            ))}
            {error && <div style={{ ...text.hint, color: theme.color.dangerText }}>{error}</div>}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button type="button" style={secondaryBtn} onClick={() => setDraft((d) => [...d!, { userId: null, name: "", role: null, attended: null }])}>Add a person</button>
              <button type="button" style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>{saving ? "Saving…" : "Save participants"}</button>
              <button type="button" style={secondaryBtn} onClick={() => setDraft(null)}>Cancel</button>
            </div>
          </div>
        </ModalOverlay>
      )}
      {!rows ? null : rows.length === 0 ? (
        <div style={{ ...text.body, color: theme.color.muted }}>No participants named yet.</div>
      ) : (
        <table style={{ ...text.body, width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} style={{ borderBottom: "1px solid var(--c-border)" }}>
                <td style={{ padding: "6px 8px", color: theme.color.text, fontWeight: 600 }}>{p.name}{p.userId ? "" : <span style={{ fontWeight: 400, color: theme.color.faint }}> (no login)</span>}</td>
                <td style={{ padding: "6px 8px", color: theme.color.muted }}>{p.role || "—"}</td>
                <td style={{ padding: "6px 8px" }}>{p.attended === true ? <StatusPill value="completed" label="Attended" /> : p.attended === false ? <StatusPill value="cancelled" label="Did not attend" /> : <span style={{ color: theme.color.faint }}>not recorded</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ── Findings ─────────────────────────────────────────────────────────────────

interface Finding {
  id: string; title: string; severity: string; detail: string | null; correctiveActionId: string | null;
  correctiveAction: { id: string; displayId: number | null; title: string; status: string } | null;
}

const FINDING_SEVERITIES = ["low", "medium", "high", "critical"] as const;

export function ExerciseFindings({ exerciseId, exerciseTitle, canEdit, onChanged }: { exerciseId: string; exerciseTitle: string; canEdit: boolean; onChanged: () => void }) {
  const [findings, setFindings] = useState<Finding[] | null>(null);
  const [editing, setEditing] = useState<Finding | "new" | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const base = `/resilience/exercises/${exerciseId}/findings`;
  const load = useCallback(async () => {
    try { setFindings((await api.get<{ findings: Finding[] }>(base)).findings); }
    catch (e) { setError(apiErrorMessage(e, "Could not load findings")); setFindings([]); }
  }, [base]);
  useEffect(() => { void load(); }, [load]);

  async function act(key: string, fn: () => Promise<unknown>) {
    setBusy(key); setError(null);
    try { await fn(); await load(); onChanged(); }
    catch (e) { setError(apiErrorMessage(e, "Action failed")); }
    finally { setBusy(null); }
  }

  // Raise the finding as a corrective action, then record the link on the finding.
  const raise = (f: Finding) => act(`raise:${f.id}`, async () => {
    const res = await api.post<{ action: { id: string } }>("/improvement/actions", {
      title: f.title, description: [f.detail, `Finding from the exercise "${exerciseTitle}" (ISO 27001 A.5.29 / A.5.30, NCSC CAF D2.b).`].filter(Boolean).join("\n\n"),
      source: "exercise", sourceRef: `exercise-finding:${f.id}`, priority: f.severity,
    });
    await api.put(`${base}/${f.id}`, { correctiveActionId: res.action.id });
  });

  const remove = (f: Finding) => {
    if (!window.confirm(`Delete the finding "${f.title}"?${f.correctiveAction ? " Its corrective action stays." : ""}`)) return;
    void act(`del:${f.id}`, () => api.del(`${base}/${f.id}`));
  };

  return (
    <section style={{ ...card, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <h3 style={{ ...h3, margin: 0 }}>Findings</h3>
        {findings && findings.length > 0 && <span style={text.hint}>{findings.length} recorded, {findings.filter((f) => !f.correctiveActionId).length} without an action</span>}
        <span style={{ flex: 1 }} />
      </div>
      {error && <div style={{ ...text.hint, color: theme.color.dangerText, marginBottom: 6 }}>{error}</div>}
      {!findings ? null : findings.length === 0 ? (
        <div style={{ ...text.body, color: theme.color.muted }}>No findings recorded. Record each one on its own so it can be raised and tracked as a corrective action.</div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {findings.map((f) => (
            <div key={f.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 10px", border: "1px solid var(--c-border)", borderRadius: 3 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={text.itemTitle}>{f.title}</span>
                  <SeverityPill value={f.severity} />
                  {f.correctiveAction && (
                    <PortalLink to={KIND_HREFS.action(f.correctiveAction.id) ?? "/improvement"} style={{ fontSize: text.hint.fontSize, color: "var(--c-accentText)" }}>
                      {f.correctiveAction.displayId != null ? `CA-${f.correctiveAction.displayId}` : "Corrective action"} ({f.correctiveAction.status.replace(/_/g, " ")})
                    </PortalLink>
                  )}
                </div>
                {f.detail && <div style={{ ...text.hint, marginTop: 3, whiteSpace: "pre-wrap" }}>{f.detail}</div>}
              </div>
              {canEdit && <EditButton onClick={() => setEditing(f)} />}
            </div>
          ))}
        </div>
      )}
      {canEdit && (
        <div style={{ marginTop: 10 }}>
          <RecordActions>
            <button type="button" style={secondaryBtn} onClick={() => setEditing("new")}>Add finding</button>
            {(findings ?? []).filter((f) => !f.correctiveActionId).map((f) => (
              <button key={f.id} type="button" style={secondaryBtn} disabled={busy === `raise:${f.id}`} onClick={() => raise(f)} title={`Raise "${f.title}" as a corrective action`}>
                {busy === `raise:${f.id}` ? "Raising…" : `Raise corrective action: ${f.title.length > 40 ? `${f.title.slice(0, 40)}…` : f.title}`}
              </button>
            ))}
          </RecordActions>
        </div>
      )}
      {editing && (
        <FindingForm
          initial={editing === "new" ? null : editing}
          onDelete={editing === "new" ? undefined : () => { const f = editing; setEditing(null); remove(f); }}
          onClose={() => setEditing(null)}
          onSave={async (body) => {
            if (editing === "new") await api.post(base, body); else await api.put(`${base}/${editing.id}`, body);
            setEditing(null); await load(); onChanged();
          }}
        />
      )}
    </section>
  );
}

function FindingForm({ initial, onClose, onSave, onDelete }: { initial: Finding | null; onClose: () => void; onSave: (b: { title: string; severity: string; detail: string | null }) => Promise<void>; onDelete?: () => void }) {
  const [title, setTitle] = useState(initial?.title ?? "");
  const [severity, setSeverity] = useState(initial?.severity ?? "medium");
  const [detail, setDetail] = useState(initial?.detail ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(e: React.FormEvent) {
    e.preventDefault(); setSaving(true); setError(null);
    try { await onSave({ title: title.trim(), severity, detail: detail.trim() || null }); }
    catch (err) { setError(apiErrorMessage(err, "Could not save the finding")); setSaving(false); }
  }
  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={initial ? "Edit finding" : "Add a finding"} onClose={onClose} />
      <form onSubmit={submit} style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Field label="Finding *" span2><input required value={title} onChange={(e) => setTitle(e.target.value)} style={input} placeholder="e.g. The offline backup was four days old" maxLength={300} /></Field>
        <Field label="Severity"><select value={severity} onChange={(e) => setSeverity(e.target.value)} style={input}>{FINDING_SEVERITIES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}</select></Field>
        <div />
        <Field label="Detail" span2><textarea rows={4} value={detail} onChange={(e) => setDetail(e.target.value)} style={textarea} placeholder="What was observed, and why it matters" /></Field>
        {error && <div style={{ ...text.body, gridColumn: "1 / -1", color: theme.color.dangerText }}>{error}</div>}
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10 }}>
          <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Save"}</button>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
          {onDelete && <><span style={{ flex: 1 }} /><button type="button" style={dangerBtn} onClick={onDelete}>Delete finding</button></>}
        </div>
      </form>
    </ModalOverlay>
  );
}
